import { CloseRounded, GitHub, LinkedIn } from '@mui/icons-material';
import { Modal } from '@mui/material';
import React from 'react'
import styled, { keyframes } from 'styled-components'

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const Container = styled.div`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    overflow-y: auto;
    transition: all 0.3s ease;
`;

const Wrapper = styled.div`
    max-width: 800px;
    width: 100%;
    border-radius: 20px;
    margin: 50px 12px;
    height: min-content;
    background: ${({ theme }) => theme.card_solid};
    border: 1px solid ${({ theme }) => theme.card_border};
    color: ${({ theme }) => theme.text_primary};
    padding: 24px;
    display: flex;
    flex-direction: column;
    position: relative;
    animation: ${fadeIn} 0.3s ease forwards;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.div`
    position: absolute;
    top: 14px;
    right: 18px;
    cursor: pointer;
    color: ${({ theme }) => theme.text_secondary};
    transition: all 0.2s ease;
    padding: 4px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        color: ${({ theme }) => theme.text_primary};
        background: ${({ theme }) => theme.primary_light};
    }
`;

const Title = styled.div`
    font-size: 26px;
    font-weight: 700;
    color: ${({ theme }) => theme.text_primary};
    margin: 8px 6px 0;
    letter-spacing: -0.3px;

    @media only screen and (max-width: 600px) {
        font-size: 22px;
    }
`;

const Date = styled.div`
    font-size: 15px;
    margin: 4px 6px;
    font-weight: 400;
    color: ${({ theme }) => theme.text_tertiary};

    @media only screen and (max-width: 768px) {
        font-size: 12px;
    }
`

const Desc = styled.div`
    font-size: 15px;
    font-weight: 400;
    color: ${({ theme }) => theme.text_secondary};
    margin: 8px 6px;
    line-height: 1.7;

    @media only screen and (max-width: 600px) {
        font-size: 14px;
    }
`;

const Image = styled.img`
    width: 100%;
    object-fit: cover;
    border-radius: 14px;
    margin-top: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const Label = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_primary};
    margin: 12px 6px 8px;
`;

const Tags = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin: 8px 0;
    gap: 6px;
`;

const Tag = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.primary};
    padding: 4px 12px;
    border-radius: 20px;
    background: ${({ theme }) => theme.primary_light};
`;

const Members = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex-wrap: wrap;
    margin: 8px 6px;
`;

const Member = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const MemberImage = styled.img`
    width: 44px;
    height: 44px;
    object-fit: cover;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => theme.card_border};

    @media only screen and (max-width: 600px) {
        width: 32px;
        height: 32px;
    }
`;

const MemberName = styled.div`
    font-size: 15px;
    font-weight: 500;
    color: ${({ theme }) => theme.text_primary};
`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: flex-end;
    margin: 16px 0 4px;
    gap: 12px;
`;

const Button = styled.a`
    width: 100%;
    text-align: center;
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme.white};
    padding: 12px 16px;
    border-radius: 12px;
    background: ${({ theme }) => theme.gradient_primary};
    cursor: pointer;
    text-decoration: none;
    transition: all 0.3s ease;
    letter-spacing: 0.3px;

    ${({ dull, theme }) => dull && `
        background: ${theme.bgLight};
        color: ${theme.text_secondary};
    `}

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 16px ${({ theme }) => theme.shadow_primary};
    }
`;

const ProjectDetails = ({ openModal, setOpenModal }) => {
    const project = openModal?.project;
    if (!project) return null;

    return (
        <Modal open={true} onClose={() => setOpenModal({ state: false, project: null })}>
            <Container>
                <Wrapper>
                    <CloseButton onClick={() => setOpenModal({ state: false, project: null })}>
                        <CloseRounded />
                    </CloseButton>
                    <Image src={project.image} alt={project.title} />
                    <Title>{project.title}</Title>
                    {project.date && <Date>{project.date}</Date>}
                    <Tags>
                        {project.tags?.map((tag, idx) => (
                            <Tag key={idx}>{tag}</Tag>
                        ))}
                    </Tags>
                    <Desc>{project.description}</Desc>
                    {project.member && (
                        <>
                            <Label>Members</Label>
                            <Members>
                                {project.member.map((member, idx) => (
                                    <Member key={idx}>
                                        <MemberImage src={member.img} alt={member.name} />
                                        <MemberName>{member.name}</MemberName>
                                        {member.github && (
                                            <a href={member.github} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'flex' }}>
                                                <GitHub fontSize="small" />
                                            </a>
                                        )}
                                        {member.linkedin && (
                                            <a href={member.linkedin} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'flex' }}>
                                                <LinkedIn fontSize="small" />
                                            </a>
                                        )}
                                    </Member>
                                ))}
                            </Members>
                        </>
                    )}
                    <ButtonGroup>
                        <Button dull href={project.github} target='_blank' rel='noopener noreferrer'>View Code</Button>
                        <Button href={project.webapp} target='_blank' rel='noopener noreferrer'>View Live App</Button>
                    </ButtonGroup>
                </Wrapper>
            </Container>
        </Modal>
    )
}

export default ProjectDetails
